import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { articles as initialArticles } from '../data/articles';
import { useMetaTags } from '../hooks/useMetaTags';
import './BlogPage.css';

export default function BlogPage({ articles = initialArticles }) {
  useMetaTags({
    title: 'Performance Insights Blog | Playermaker Canada | iLOVEthisGAME',
    description: 'Explore expert soccer wearable analysis, Playermaker 2.0 tracking guides, two-footed player development tutorials, and Canadian youth soccer news.',
    image: '/playermaker_box_transparent.webp',
    url: '/blog',
    type: 'website'
  });

  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');

  // Dynamically derive category filter list from actual articles dataset
  const categories = ['ALL', ...new Set(articles.map(a => a.category).filter(Boolean))];

  const filteredArticles = articles.filter(article => {
    const matchesCategory = selectedCategory === 'ALL' || article.category === selectedCategory;
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          article.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          article.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featured = articles[0];

  return (
    <div className="blog-page-wrapper">
      <div className="container">

        {/* Page Header */}
        <div className="blog-page-header">
          <span className="badge-volt pulse-glow-volt">KNOWLEDGE HUB</span>
          <h1 className="blog-page-title">PERFORMANCE INSIGHTS &amp; NEWS</h1>
          <p className="blog-page-subtitle">
            In-depth tactical guides, equipment telemetry deep dives, and Canadian soccer player pathways.
          </p>
        </div>

        {/* Featured Article Banner */}
        {selectedCategory === 'ALL' && searchQuery === '' && featured && (
          <div className="featured-article-card card-tech">
            {featured.heroImage && (
              <div className="featured-image-wrap">
                <Link to={`/blog/${featured.id}`}>
                  <img src={featured.heroImage} alt={featured.heroImageAlt || featured.title} className="featured-image" loading="lazy" />
                </Link>
              </div>
            )}
            <div className="featured-content">
              <div className="featured-badge-row">
                <span className="badge-volt">FEATURED ARTICLE</span>
                <span className="featured-category">{featured.category}</span>
              </div>
              <h2 className="featured-title">
                <Link to={`/blog/${featured.id}`} style={{ color: 'inherit', textDecoration: 'none' }}>
                  {featured.title}
                </Link>
              </h2>
              <p className="featured-excerpt">{featured.excerpt}</p>
              <div className="featured-meta">
                <span>{featured.date}</span>
                <span>•</span>
                <span>{featured.readTime}</span>
              </div>
              <Link to={`/blog/${featured.id}`} className="btn btn-volt featured-btn" style={{ textDecoration: 'none', display: 'inline-block' }}>
                Read Full Article &rarr;
              </Link>
            </div>
          </div>
        )}

        {/* Controls: Search & Category Filter */}
        <div className="blog-controls card-tech">
          <div className="search-bar-wrapper">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"/>
              <line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input 
              type="text" 
              placeholder="Search articles by title, topic, or keyword..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="blog-search-input"
            />
            {searchQuery && (
              <button className="search-clear-btn" onClick={() => setSearchQuery('')}>✕</button>
            )}
          </div>

          <div className="category-filter-pills">
            {categories.map(cat => (
              <button
                key={cat}
                className={`filter-pill ${selectedCategory === cat ? 'active' : ''}`}
                onClick={() => setSelectedCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Article Grid */}
        <div className="articles-section-title">
          <h3>
            {selectedCategory === 'ALL' ? 'ALL ARTICLES' : selectedCategory} 
            <span className="article-count">({filteredArticles.length})</span>
          </h3>
        </div>

        {filteredArticles.length === 0 ? (
          <div className="no-articles-found card-tech text-center">
            <h3>No articles found</h3>
            <p>No posts matched your search "{searchQuery}". Try clearing filters.</p>
            <button className="btn btn-outline" onClick={() => { setSearchQuery(''); setSelectedCategory('ALL'); }}>
              Reset Filters
            </button>
          </div>
        ) : (
          <div className="blog-grid full-grid">
            {filteredArticles.map(article => (
              <article key={article.id} className="card-tech blog-card">
                {article.heroImage && (
                  <div className="blog-card-image-wrap">
                    <Link to={`/blog/${article.id}`}>
                      <img src={article.heroImage} alt={article.heroImageAlt || article.title} className="blog-card-image" loading="lazy" />
                    </Link>
                  </div>
                )}
                <div className="blog-card-content">
                  <div className="blog-meta">
                    <span className="blog-category">{article.category}</span>
                    <span className="blog-read-time">{article.readTime}</span>
                  </div>
                  
                  <h3 className="blog-card-title">
                    <Link to={`/blog/${article.id}`}>
                      {article.title}
                    </Link>
                  </h3>
                  
                  <p className="blog-excerpt">{article.excerpt}</p>
                  
                  <div className="blog-footer">
                    <span className="blog-date">{article.date}</span>
                    <Link to={`/blog/${article.id}`} className="blog-read-link">
                      Read Article &rarr;
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}
